import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod/v4";
import {
  TextInput,
  NumberInput,
  Select,
  MultiSelect,
  Checkbox,
  Button,
  Grid,
  Title,
  Stack,
  Group,
} from "@mantine/core";
import type { Filter } from "@/api/types";
import { AMENITY_OPTIONS } from "./filterOptions";

const filterSchema = z.object({
  name: z.string().min(1, "Name is required"),
  discord_channel_id: z.string().min(1, "Discord channel ID is required"),
  search_area: z.string().optional(),
  min_rent: z.coerce.number().optional(),
  max_rent: z.coerce.number().optional(),
  min_rooms: z.coerce.number().optional(),
  max_rooms: z.coerce.number().optional(),
  min_size_sqm: z.coerce.number().optional(),
  max_size_sqm: z.coerce.number().optional(),
  home_type: z.string().optional(),
  furnished: z.string().optional(),
  amenities_required: z.array(z.string()).optional(),
  electricity_included: z.boolean().optional(),
  heating_included: z.boolean().optional(),
  internet_included: z.boolean().optional(),
  metro_green: z.boolean().optional(),
  metro_red: z.boolean().optional(),
  metro_blue: z.boolean().optional(),
  max_metro_distance_m: z.coerce.number().optional(),
  max_bus_distance_m: z.coerce.number().optional(),
  allow_bus_access: z.boolean().optional(),
  pets_required: z.boolean().optional(),
  until_further_notice: z.boolean().optional(),
  require_first_hand: z.boolean().optional(),
  is_active: z.boolean().optional(),
});

type FilterFormData = z.infer<typeof filterSchema>;

interface FilterFormProps {
  filter?: Filter;
  onSubmit: (data: Partial<Filter>) => void;
  isSubmitting?: boolean;
}

export function FilterForm({ filter, onSubmit, isSubmitting }: FilterFormProps) {
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<FilterFormData>({
    resolver: zodResolver(filterSchema) as never,
    defaultValues: {
      name: filter?.name || "",
      discord_channel_id: filter?.discord_channel_id || "",
      search_area: filter?.search_area || "",
      min_rent: filter?.min_rent || undefined,
      max_rent: filter?.max_rent || undefined,
      min_rooms: filter?.min_rooms || undefined,
      max_rooms: filter?.max_rooms || undefined,
      min_size_sqm: filter?.min_size_sqm || undefined,
      max_size_sqm: filter?.max_size_sqm || undefined,
      home_type: filter?.home_type || "",
      furnished: filter?.furnished === true ? "true" : filter?.furnished === false ? "false" : "",
      amenities_required: filter?.amenities_required || [],
      electricity_included: filter?.electricity_included || false,
      heating_included: filter?.heating_included || false,
      internet_included: filter?.internet_included || false,
      metro_green: filter?.metro_lines_required?.includes("green"),
      metro_red: filter?.metro_lines_required?.includes("red"),
      metro_blue: filter?.metro_lines_required?.includes("blue"),
      max_metro_distance_m: filter?.max_metro_distance_m ?? 1000,
      max_bus_distance_m: filter?.max_bus_distance_m ?? 500,
      allow_bus_access: filter?.allow_bus_access ?? false,
      pets_required: filter?.pets_required || false,
      until_further_notice: filter?.until_further_notice || false,
      require_first_hand: filter?.require_first_hand || false,
      is_active: filter?.is_active ?? true,
    },
  });

  const processFormData = (data: FilterFormData): Partial<Filter> => {
    const metroLines: string[] = [];
    if (data.metro_green) metroLines.push("green");
    if (data.metro_red) metroLines.push("red");
    if (data.metro_blue) metroLines.push("blue");

    return {
      name: data.name,
      discord_channel_id: data.discord_channel_id,
      search_area: data.search_area || undefined,
      min_rent: data.min_rent || undefined,
      max_rent: data.max_rent || undefined,
      min_rooms: data.min_rooms || undefined,
      max_rooms: data.max_rooms || undefined,
      min_size_sqm: data.min_size_sqm || undefined,
      max_size_sqm: data.max_size_sqm || undefined,
      home_type: data.home_type || undefined,
      furnished: data.furnished === "true" ? true : data.furnished === "false" ? false : undefined,
      amenities_required:
        data.amenities_required && data.amenities_required.length > 0
          ? data.amenities_required
          : undefined,
      electricity_included: data.electricity_included || undefined,
      heating_included: data.heating_included || undefined,
      internet_included: data.internet_included || undefined,
      metro_lines_required: metroLines.length > 0 ? metroLines : undefined,
      max_metro_distance_m:
        data.max_metro_distance_m === undefined ? undefined : data.max_metro_distance_m,
      max_bus_distance_m:
        data.max_bus_distance_m === undefined ? undefined : data.max_bus_distance_m,
      allow_bus_access: data.allow_bus_access || undefined,
      pets_required: data.pets_required || undefined,
      until_further_notice: data.until_further_notice || undefined,
      require_first_hand: data.require_first_hand || undefined,
      is_active: data.is_active ?? true,
    };
  };

  return (
    <form onSubmit={handleSubmit((data) => onSubmit(processFormData(data)))}>
      <Stack gap="lg">
        <Grid>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <TextInput label="Filter Name" {...register("name")} error={errors.name?.message} />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <TextInput
              label="Discord Channel ID"
              {...register("discord_channel_id")}
              error={errors.discord_channel_id?.message}
            />
          </Grid.Col>
        </Grid>

        <Title order={4}>Location</Title>
        <TextInput
          label="Search Area"
          {...register("search_area")}
          placeholder="e.g., Vasastaden~Stockholm_Municipality~se"
        />

        <Title order={4}>Price & Size</Title>
        <Grid>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Controller
              name="min_rent"
              control={control}
              render={({ field }) => (
                <NumberInput
                  label="Min Rent (kr)"
                  {...field}
                  value={field.value ?? ""}
                  onChange={(val) => field.onChange(val || undefined)}
                />
              )}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Controller
              name="max_rent"
              control={control}
              render={({ field }) => (
                <NumberInput
                  label="Max Rent (kr)"
                  {...field}
                  value={field.value ?? ""}
                  onChange={(val) => field.onChange(val || undefined)}
                />
              )}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Controller
              name="min_rooms"
              control={control}
              render={({ field }) => (
                <NumberInput
                  label="Min Rooms"
                  step={0.5}
                  {...field}
                  value={field.value ?? ""}
                  onChange={(val) => field.onChange(val || undefined)}
                />
              )}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Controller
              name="max_rooms"
              control={control}
              render={({ field }) => (
                <NumberInput
                  label="Max Rooms"
                  step={0.5}
                  {...field}
                  value={field.value ?? ""}
                  onChange={(val) => field.onChange(val || undefined)}
                />
              )}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Controller
              name="min_size_sqm"
              control={control}
              render={({ field }) => (
                <NumberInput
                  label="Min Size (m²)"
                  {...field}
                  value={field.value ?? ""}
                  onChange={(val) => field.onChange(val || undefined)}
                />
              )}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Controller
              name="max_size_sqm"
              control={control}
              render={({ field }) => (
                <NumberInput
                  label="Max Size (m²)"
                  {...field}
                  value={field.value ?? ""}
                  onChange={(val) => field.onChange(val || undefined)}
                />
              )}
            />
          </Grid.Col>
        </Grid>

        <Title order={4}>Property Type</Title>
        <Grid>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Controller
              name="home_type"
              control={control}
              render={({ field }) => (
                <Select
                  label="Home Type"
                  {...field}
                  value={field.value || null}
                  onChange={(val) => field.onChange(val || "")}
                  data={[
                    { value: "", label: "Any" },
                    { value: "apartment", label: "Apartment" },
                    { value: "house", label: "House" },
                    { value: "room", label: "Room" },
                  ]}
                />
              )}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Controller
              name="furnished"
              control={control}
              render={({ field }) => (
                <Select
                  label="Furnished"
                  {...field}
                  value={field.value || null}
                  onChange={(val) => field.onChange(val || "")}
                  data={[
                    { value: "", label: "Any" },
                    { value: "true", label: "Yes" },
                    { value: "false", label: "No" },
                  ]}
                />
              )}
            />
          </Grid.Col>
        </Grid>

        <Title order={4}>Amenities Required</Title>
        <Controller
          name="amenities_required"
          control={control}
          render={({ field }) => (
            <MultiSelect
              label="Select required amenities"
              placeholder="Pick amenities"
              data={AMENITY_OPTIONS}
              {...field}
              value={field.value || []}
              onChange={(val) => field.onChange(val)}
              searchable
              clearable
            />
          )}
        />

        <Title order={4}>Utilities Included</Title>
        <Group gap="md">
          <Checkbox label="Electricity" {...register("electricity_included")} />
          <Checkbox label="Heating" {...register("heating_included")} />
          <Checkbox label="Internet" {...register("internet_included")} />
        </Group>

        <Title order={4}>Transport</Title>
        <Stack gap="sm">
          <Group gap="md">
            <Checkbox label="Green Metro Line" {...register("metro_green")} />
            <Checkbox label="Red Metro Line" {...register("metro_red")} />
            <Checkbox label="Blue Metro Line" {...register("metro_blue")} />
          </Group>
          <Grid>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <Controller
                name="max_metro_distance_m"
                control={control}
                render={({ field }) => (
                  <NumberInput
                    label="Maximum Distance to Metro (m)"
                    description="Maximum walking distance to an accepted metro stop"
                    min={0}
                    {...field}
                    value={field.value ?? ""}
                    onChange={(value) => field.onChange(value === "" ? undefined : Number(value))}
                  />
                )}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <Controller
                name="max_bus_distance_m"
                control={control}
                render={({ field }) => (
                  <NumberInput
                    label="Maximum Distance to Bus (m)"
                    description="Used as an alternative limit when Allow Bus Access is enabled"
                    min={0}
                    {...field}
                    value={field.value ?? ""}
                    onChange={(value) => field.onChange(value === "" ? undefined : Number(value))}
                  />
                )}
              />
            </Grid.Col>
          </Grid>
          <Checkbox
            label="Allow Bus Access"
            description="Also accept apartments within the configured bus distance"
            {...register("allow_bus_access")}
          />
        </Stack>

        <Title order={4}>Other</Title>
        <Stack gap="xs">
          <Checkbox label="Pets Allowed" {...register("pets_required")} />
          <Checkbox
            label="Until Further Notice Only"
            description="Only show apartments with no specific end date"
            {...register("until_further_notice")}
          />
          <Checkbox
            label="First Hand Only"
            description="Only show first hand rental contracts (not sublets)"
            {...register("require_first_hand")}
          />
          {filter && <Checkbox label="Active" {...register("is_active")} />}
        </Stack>

        <Button type="submit" loading={isSubmitting}>
          {filter ? "Update Filter" : "Create Filter"}
        </Button>
      </Stack>
    </form>
  );
}
